/**
 * ProjectDateInputsTestPage - Simplified Testing Harness
 *
 * Purpose: Test core date calculation logic
 *
 * Test Flow:
 * 1. Component auto-populates with next year + summer schedule on mount
 * 2. User can modify tax year or schedule
 * 3. Click "Validate" to verify calculations
 * 4. See pass/fail results
 *
 * Note: No persistence - refreshing resets to auto-populated defaults
 *
 * Validates:
 * - Tax year + schedule → correct fiscal year end
 * - Tax year + schedule → correct payment dates
 * - Revenue percentages sum to 100%
 */

import { Badge, Box, Button, Code, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';

import { ProjectDateInputs } from '~/builders/project-dates/ProjectDateInputs';
import { SidebarProvider } from '~/components/layout/shared/SidebarContext';
import type { DateInputsForm } from '~/types';

interface TestAssertion {
  name: string;
  actual: string;
  expected: string;
  passed: boolean;
}

interface TestResult {
  name: string;
  passed: boolean;
  assertions: TestAssertion[];
}

export const ProjectDateInputsTestPage = () => {
  const [values, setValues] = useState<DateInputsForm>({
    startingTaxYear: '',
    fiscalYearEnd: '',
    paymentDates: [],
    taxCollectionSchedule: undefined,
  });

  const [testResults, setTestResults] = useState<TestResult[]>([]);

  const validateCurrentState = () => {
    if (!values.startingTaxYear || !values.taxCollectionSchedule) {
      alert('Please enter a tax year and select a schedule (Summer or Winter)');
      return;
    }

    const year = parseInt(values.startingTaxYear, 10);
    const schedule = values.taxCollectionSchedule;
    const assertions: TestAssertion[] = [];

    // 1. Validate Fiscal Year End
    const expectedFYE = schedule === 'summer' ? `${year + 1}-06-30` : `${year + 1}-11-01`;
    assertions.push({
      name: 'Fiscal year end',
      actual: values.fiscalYearEnd,
      expected: expectedFYE,
      passed: values.fiscalYearEnd === expectedFYE,
    });

    // 2. Validate Payment Dates
    if (schedule === 'summer') {
      assertions.push({
        name: 'First payment date (November)',
        actual: values.paymentDates[0]?.date || '',
        expected: `${year}-11-01`,
        passed: values.paymentDates[0]?.date === `${year}-11-01`,
      });
      assertions.push({
        name: 'Second payment date (May)',
        actual: values.paymentDates[1]?.date || '',
        expected: `${year + 1}-05-01`,
        passed: values.paymentDates[1]?.date === `${year + 1}-05-01`,
      });
    } else {
      assertions.push({
        name: 'First payment date (May)',
        actual: values.paymentDates[0]?.date || '',
        expected: `${year + 1}-05-01`,
        passed: values.paymentDates[0]?.date === `${year + 1}-05-01`,
      });
      assertions.push({
        name: 'Second payment date (November)',
        actual: values.paymentDates[1]?.date || '',
        expected: `${year + 1}-11-01`,
        passed: values.paymentDates[1]?.date === `${year + 1}-11-01`,
      });
    }

    // 3. Validate Revenue Percentage Sum = 100%
    const totalPercentage = values.paymentDates.reduce((sum, payment) => {
      return sum + (parseFloat(payment.revenuePercentage || '0') || 0);
    }, 0);

    assertions.push({
      name: 'Revenue percentages sum to 100%',
      actual: totalPercentage.toFixed(1),
      expected: '100.0',
      passed: Math.abs(totalPercentage - 100) < 0.01,
    });

    const testResult: TestResult = {
      name: `${schedule.charAt(0).toUpperCase() + schedule.slice(1)} Schedule - Tax Year ${year}`,
      passed: assertions.every((a) => a.passed),
      assertions,
    };

    setTestResults([testResult]);
  };

  return (
    <Box p={8} maxW="1400px" mx="auto">
      <VStack align="stretch" gap={8}>
        {/* Header */}
        <Box>
          <Heading size="xl" mb={2}>
            ProjectDateInputs - Test Page
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Enter a tax year, select a schedule, and click Validate to verify date calculations.
          </Text>
        </Box>

        {/* Component Under Test */}
        <Box>
          <Heading size="md" mb={4}>
            Component
          </Heading>
          <SidebarProvider>
            <ProjectDateInputs values={values} onUpdate={setValues} showValidation={false} />
          </SidebarProvider>
        </Box>

        {/* Validate Button */}
        <HStack justify="flex-end" gap={4}>
          <Button
            size="lg"
            onClick={() =>
              setValues({
                startingTaxYear: '',
                fiscalYearEnd: '',
                paymentDates: [],
                taxCollectionSchedule: undefined,
              })
            }
            colorPalette="gray"
          >
            Reset to Defaults
          </Button>
          <Button size="lg" onClick={validateCurrentState} colorPalette="green">
            ✓ Validate Calculations
          </Button>
        </HStack>

        {/* Test Results */}
        {testResults.length > 0 && (
          <Box>
            <Heading size="md" mb={4}>
              Validation Results
            </Heading>
            <VStack align="stretch" gap={4}>
              {testResults.map((result) => (
                <Box
                  key={result.name}
                  p={4}
                  bg={result.passed ? 'green.50' : 'red.50'}
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderColor={result.passed ? 'green.500' : 'red.500'}
                >
                  <HStack mb={3}>
                    <Badge colorScheme={result.passed ? 'green' : 'red'} fontSize="sm">
                      {result.passed ? '✅ ALL CHECKS PASSED' : '❌ SOME CHECKS FAILED'}
                    </Badge>
                    <Text fontWeight="bold">{result.name}</Text>
                  </HStack>
                  <VStack align="flex-start" gap={2} fontSize="sm">
                    {result.assertions.map((assertion) => (
                      <HStack key={assertion.name} fontFamily="mono" fontSize="xs">
                        <Text>{assertion.passed ? '✅' : '❌'}</Text>
                        <Text fontWeight="medium" minW="200px">
                          {assertion.name}:
                        </Text>
                        <Text color={assertion.passed ? 'green.700' : 'red.700'}>
                          {assertion.actual}
                        </Text>
                        {!assertion.passed && (
                          <>
                            <Text color="gray.500">(expected:</Text>
                            <Text color="gray.700">{assertion.expected})</Text>
                          </>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Reference Documentation */}
        <Box
          p={6}
          bg="blue.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="blue.500"
          mt={8}
        >
          <Heading size="sm" mb={4}>
            📘 Date Calculation Reference
          </Heading>
          <Text fontSize="sm" mb={4} color="gray.700">
            How tax year and collection schedule determine all other dates:
          </Text>
          <VStack align="stretch" gap={4} fontSize="sm">
            {/* Summer Schedule */}
            <Box>
              <Text fontWeight="bold" mb={2} color="blue.700">
                Summer Schedule
              </Text>
              <Code display="block" p={3} fontSize="xs" whiteSpace="pre" borderRadius="md">
                {`Tax Year: YYYY

Fiscal Year End: (YYYY+1)-06-30
├─ Example: Tax Year 2025 → Fiscal Year End: 2026-06-30

Payment Dates:
├─ First:  YYYY-11-01     (November of SAME year)
│  └─ Example: Tax Year 2025 → 2025-11-01
└─ Second: (YYYY+1)-05-01 (May of NEXT year)
   └─ Example: Tax Year 2025 → 2026-05-01

Revenue Percentages: Must sum to 100%
├─ Default: 70% first, 30% second
└─ User can modify as needed`}
              </Code>
            </Box>

            {/* Winter Schedule */}
            <Box>
              <Text fontWeight="bold" mb={2} color="purple.700">
                Winter Schedule
              </Text>
              <Code display="block" p={3} fontSize="xs" whiteSpace="pre" borderRadius="md">
                {`Tax Year: YYYY

Fiscal Year End: (YYYY+1)-11-01
├─ Example: Tax Year 2025 → Fiscal Year End: 2026-11-01

Payment Dates:
├─ First:  (YYYY+1)-05-01 (May of NEXT year)
│  └─ Example: Tax Year 2025 → 2026-05-01
└─ Second: (YYYY+1)-11-01 (November of NEXT year)
   └─ Example: Tax Year 2025 → 2026-11-01

Revenue Percentages: Must sum to 100%
├─ Default: 70% first, 30% second
└─ User can modify as needed`}
              </Code>
            </Box>

            {/* Key Insight */}
            <Box
              p={3}
              bg="yellow.50"
              borderRadius="md"
              borderLeft="3px solid"
              borderColor="yellow.500"
            >
              <Text fontWeight="bold" fontSize="xs" mb={1} color="yellow.800">
                💡 Key Insight
              </Text>
              <Text fontSize="xs" color="yellow.900">
                Revenue percentages stay in <strong>position</strong> (first/second), not by{' '}
                <strong>month</strong>
                (Nov/May). When you switch schedules, the dates reorder but percentages remain:
                first payment = 70%, second payment = 30%.
              </Text>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};
